import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * PwaInstallPrompt — captures Chrome/Edge/Android beforeinstallprompt event
 * and shows a lightweight banner. Also detects iOS Safari (no auto-prompt available)
 * and shows step-by-step instructions instead.
 * Dismisses persist in localStorage so we don't nag returning users.
 */
export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosBanner, setIosBanner] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("gtf_pwa_dismissed") === "1") return;
    // Already installed?
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Detect iOS Safari — Apple never fires beforeinstallprompt
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (isIOS && isSafari && !window.navigator.standalone) {
      // Wait a beat so it doesn't shove into the very first paint
      setTimeout(() => setIosBanner(true), 4000);
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem("gtf_pwa_dismissed", "1");
    setVisible(false);
    setIosBanner(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") setVisible(false);
    setDeferred(null);
  };

  if (!visible && !iosBanner) return null;
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[100]">
      <div className="rounded-2xl bg-white shadow-2xl border border-gray-100 p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 text-sm">
          {iosBanner ? (
            <>
              <div className="font-semibold text-gray-900">Install GTF on your iPhone</div>
              <div className="text-xs text-gray-600 mt-1">Tap the <b>Share</b> icon in Safari, then <b>Add to Home Screen</b> — opens like a real app.</div>
            </>
          ) : (
            <>
              <div className="font-semibold text-gray-900">Install GTF</div>
              <div className="text-xs text-gray-600 mt-0.5">Add to your home screen. Works offline, opens faster, no browser bar.</div>
              <button data-testid="pwa-install-btn" onClick={install}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">
                <Download className="h-3 w-3" />Install
              </button>
            </>
          )}
        </div>
        <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
